import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import _ from 'lodash';
import { dateOnlyFormat, monthOnlyFormat } from '@/utils/date';

interface QuestionComponent {
  object_id: string;
  title: string;
  more_information_content: string;
  has_more_information: boolean;
  subtitle: string;
  value: {
    answer?: any;
    question: Question;
    parent_answer?: any; // todo
  };
  type: string;
  parent?: any;
  internal_annotation: string;
  options?: [] | Record<string, any>;
  settings?: Record<string, any>;
}

const state: QuestionnaireState = {
  originQuestionnaire: null,
  questionnaire: {
    object_id: '',
    question_groups: [],
  },
  questionGroupIndex: 0,
  questionIndex: 0,
  iterationIndex: 0,
  userProfile: {
    weight: 0,
    height: 0,
    age: 0,
  },
  currentRule: null,
  questionGroupQuestions: [],
};

const getters: GetterTree<QuestionnaireState, RootState> = {
  // Private Use
  // deprecated
  evalConditionEquationIsNotEmpty: (state, getters) => (
    question,
    rule: QQuestionCondition | null = null,
    newAnswer = null
  ) => {
    if (!rule) {
      return false;
    }

    let equationResult = false;
    // remap all conditions into an array, each item is [key => condition.object_id, value => condition]
    const conditions = {};
    for (const item of rule.conditions) {
      if (item.object_id) {
        conditions[item.object_id] = item;
      }
    }

    const objectIds = rule.condition_equation.match(
      /qdc_[a-zA-Z0-9]+|qnrc_[a-zA-Z0-9]+/g
    );
    const uniqueObjectIds = objectIds.filter(function(item, index) {
      return objectIds.indexOf(item) == index;
    });
    if (uniqueObjectIds) {
      let equation = rule.condition_equation as string;
      // loop each condition.object_id found in the equation string
      for (const objectId of uniqueObjectIds) {
        const condition = conditions[objectId];
        let conditionResult = false;
        // if the condition model exists, then evaluate the result
        if (condition) {
          conditionResult = getters.evalQuestionCondition(
            question,
            condition,
            newAnswer
          );
        }
        // otherwise, the condition.object_id in equation string is not found in the condition model
        // keep the var isMatched = false
        const search = objectId.toString();
        equation = equation.replaceAll(search, conditionResult.toString());
      }
      console.debug(equation);
      equationResult = eval(equation);
    }
    return equationResult;
  },
  // deprecated
  evalConditionEquationIsEmpty: (state, getters) => (
    question,
    rule: QQuestionCondition | null = null,
    newAnswer = null
  ) => {
    if (!rule) {
      return false;
    }

    const conditions = rule.conditions.map((condition: QQuestionCondition) => {
      return getters.evalQuestionCondition(question, condition, newAnswer);
    });
    return conditions.every((item) => item == true);
  },
  // deprecated
  evalQuestionCondition: (state, getters, rootState, rootGetter) => (
    currentQuestion,
    condition,
    newAnswer
  ) => {
    let value = newAnswer || currentQuestion.value.answer;

    if (condition.source_type === 'profile_data') {
      value = rootGetter['CandidateProfile/retrieveDataFromProfile'](
        condition.source_key.toLowerCase()
      );
      if (typeof value === 'string' || value instanceof String) {
        value = value.split(',');
      }
    } else if (condition.source_type === 'risk_profile') {
      const sourceOid = condition.source_oid;
      const riskProfileItem = rootGetter[
        'RiskProfileSection/riskProfileItems'
      ].find((item) => item.object_id === sourceOid);
      // Get edge attribute from the risk profile item
      if (riskProfileItem) {
        value =
          riskProfileItem['edge_' + condition.source_key].toString() ||
          'undefined';
      } else {
        return false;
      }
    } else if (condition.source_type === 'question') {
      const sourceOid = condition.source_oid;
      let question = currentQuestion;

      // if the source oid does not exist, the result of this condition is going to be false
      // it is probably a configuration error
      if (!sourceOid) {
        return false;
      }

      if (question.object_id !== sourceOid) {
        question = getters
          .getCurrentQuestionGroup()
          .find((question) => question.object_id === sourceOid);
        if (question) {
          value = question.value.answer || 'undefined';
        } else {
          // if the question does not exist, the result of this condition is going to be false
          // this problem could be in the question group preview page, when someone tries to view the result before completing all the questions,
          // some questions are not in the question group array yet because they are affected by the question dependencies
          return false;
        }
      }

      switch (question.type) {
        case 'checkbox':
        case 'dropdown_multiple':
          if (typeof value === 'string' || value instanceof String) {
            value = value.split(',');
          }
          break;
        default:
          break;
      }
    }

    if (value === null) {
      return false;
    }

    let isMatched = false;
    switch (condition.matching_operator) {
      case 'any':
        isMatched = true;
        break;
      case 'equals':
        // e.g.
        // matching_value = [a, b]
        // value = [a, b]
        if (Array.isArray(condition.matching_value) && Array.isArray(value)) {
          isMatched = value.every((a: string) =>
            condition.matching_value.includes(a)
          );
          // e.g.
          // matching_value = a
          // value = a
        } else if (
          !Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched =
            value.toString().trim() ===
            condition.matching_value.toString().trim();
        }
        break;
      case 'not_equals':
        if (Array.isArray(condition.matching_value) && Array.isArray(value)) {
          isMatched = !value.every((a: string) =>
            condition.matching_value.includes(a)
          );
        } else if (
          !Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched =
            value.toString().trim() !==
            condition.matching_value.toString().trim();
        }
        break;
      case 'includes':
        if (Array.isArray(condition.matching_value) && Array.isArray(value)) {
          isMatched = value.some((a: string) =>
            condition.matching_value.includes(a)
          );
          // e.g.
          // matching_value = [a, b]
          // value = a
        } else if (
          Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched = condition.matching_value.includes(
            value.toString().trim()
          );
          // e.g.
          // matching_value = a
          // value = [a, b]
        } else if (
          !Array.isArray(condition.matching_value) &&
          Array.isArray(value)
        ) {
          isMatched = value.includes(
            condition.matching_value.toString().trim()
          );
        } else if (
          !Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched = value.includes(
            condition.matching_value.toString().trim()
          );
        }
        break;
      case 'not_includes':
        if (Array.isArray(condition.matching_value) && Array.isArray(value)) {
          isMatched = !value.some((a: string) =>
            condition.matching_value.includes(a)
          );
          // e.g.
          // matching_value = [a, b]
          // value = a
        } else if (
          Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched = !condition.matching_value.includes(
            value.toString().trim()
          );
          // e.g.
          // matching_value = a
          // value = [a, b]
        } else if (
          !Array.isArray(condition.matching_value) &&
          Array.isArray(value)
        ) {
          isMatched = !value.includes(
            condition.matching_value.toString().trim()
          );
        } else if (
          !Array.isArray(condition.matching_value) &&
          !Array.isArray(value)
        ) {
          isMatched = !value.includes(
            condition.matching_value.toString().trim()
          );
        }
        break;
      case 'greater_than':
        isMatched =
          parseFloat(value as string) >
          parseFloat(condition.matching_value as string);
        break;
      case 'greater_than_or_equal_to':
        isMatched =
          parseFloat(value as string) >=
          parseFloat(condition.matching_value as string);
        break;
      case 'less_than':
        isMatched =
          parseFloat(value as string) <
          parseFloat(condition.matching_value as string);
        break;
      case 'less_than_or_equal_to':
        isMatched =
          parseFloat(value as string) <=
          parseFloat(condition.matching_value as string);
        break;
      case 'retrieve_value':
        isMatched = value;
        break;
      default:
        break;
    }

    console.debug(
      `Condition: ${value} ${condition.matching_operator} ${condition.matching_value}`
    );
    console.debug(`Result: ${isMatched}`);

    // Additional condition to check an additional attribute (looped_by_value) for the current question
    if (isMatched && condition.source_type === 'question') {
      const sourceOid = condition.source_oid;
      if (currentQuestion.parent) {
        // Check whether the parent question has loop method or not
        const parent = currentQuestion.parent;
        const isLoopedByParent =
          parent.rules.findIndex((rule) => rule.loop_method !== 'none') > -1;
        if (isLoopedByParent && parent.object_id === sourceOid) {
          // Check if the looped_by_value attribute is also in the matching_value
          return condition.matching_value.includes(parent.looped_by_value);
        }
      }
    }

    return isMatched;
  },
  // To map the question group data with specific keys that are required by the library components such as name to title
  mapQuestionGroup: (state, getters) => (group) => {
    const questionGroup = {
      object_id: group.object_id,
      title: group.name,
      description: group.description || '',
      progress: group.progress || 0,
    };

    if (group.questions) {
      questionGroup['questions'] = group.questions.map(getters.mapQuestion);
    }

    return questionGroup;
  },
  // To map the question data with specific keys that are required by the library components such as name to title
  mapQuestion: () => (questionModel) => {
    const question: QuestionComponent = {
      object_id: questionModel.object_id,
      title: questionModel.body,
      more_information_content: questionModel.more_information_content,
      has_more_information: questionModel.has_more_information,
      subtitle: questionModel.object ? questionModel.object.toUpperCase() : '',
      value: {
        answer: null,
        question: questionModel,
        parent_answer: null,
      },
      type: questionModel.form_type,
      parent: null,
      internal_annotation: questionModel.internal_annotation,
    };

    if (questionModel.question_answer) {
      question.value = {
        ...questionModel.question_answer,
        question: questionModel,
      };
    }

    if (questionModel.parent_answer) {
      question.value.parent_answer = questionModel.parent_answer;
    }

    if (questionModel.answer_options && questionModel.answer_options.length) {
      question.options = questionModel.answer_options.map((option: any) => {
        return {
          title: option.display_name,
          value: option.key,
          is_exclusive: option.is_exclusive ?? false,
          show_other_options_position:
            option.show_other_options_position ?? null,
          show_other_options_in_report:
            option.show_other_options_in_report ?? false,
        };
      });
    }

    if (
      !questionModel.answer_options?.length &&
      questionModel.raw_form_options &&
      questionModel.raw_form_options.length > 0
    ) {
      question.options = {};
      for (const option of questionModel.raw_form_options) {
        question.options[option.key] = option.value;

        if (['numberinput', 'rangeslider'].includes(question.type)) {
          const value = parseFloat(option.value);

          if (!_.isNaN(value)) {
            question['options'][option.key] = value;
          }
        }
      }
    } else if (questionModel.answer_options?.length) {
      question.settings = {};
      for (const option of questionModel.raw_form_options) {
        question['settings'][option.key] = option.value;
      }
    }

    if (questionModel.form_type === 'datepicker') {
      if (questionModel.raw_form_options.range) {
        if (questionModel.raw_form_options.date_format === 'mm/yy') {
          question.value = {
            ...question.value,
            answer: [
              monthOnlyFormat(question.value.answer[0]),
              monthOnlyFormat(question.value.answer[1]),
            ],
          };
        } else {
          question.value = {
            ...question.value,
            answer: [
              dateOnlyFormat(question.value.answer[0]),
              dateOnlyFormat(question.value.answer[1]),
            ],
          };
        }
      } else {
        if (questionModel.raw_form_options.date_format === 'mm/yy') {
          question.value = {
            ...question.value,
            answer: monthOnlyFormat(question.value.answer),
          };
        } else {
          question.value = {
            ...question.value,
            answer: dateOnlyFormat(question.value.answer),
          };
        }
      }
    }

    return question;
  },
  // deprecated
  mapPreviewQuestion: (state, getters, rootState) => (target) => {
    const questionModel = target.bridge;
    const question = {
      object_id: questionModel.object_id,
      title: questionModel.body,
      subtitle: questionModel.object ? questionModel.object.toUpperCase() : '',
      value: {
        answer: null,
        question: questionModel,
      },
      type: questionModel.form_type,
      parent: null,
      rules: [],
      internal_annotation: questionModel.internal_annotation,
    };

    if (rootState['QuestionnaireAnswer'].questionnaireAnswer.question_answers) {
      const questionAnswer = rootState[
        'QuestionnaireAnswer'
      ].questionnaireAnswer.question_answers.find(
        (qa) => qa.question.object_id === questionModel.object_id
      );
      if (questionAnswer) {
        question.value = questionAnswer;
      }
    }

    if (questionModel.question_dependencies) {
      question['rules'] = questionModel.question_dependencies
        .sort((a, b) => a.display_order - b.display_order)
        .map(getters.mapRules);
    }

    if (questionModel.question_negative_rules) {
      question['negative_rules'] = questionModel.question_negative_rules
        .sort((a, b) => a.display_order - b.display_order)
        .map(getters.mapNegativeRules);
    }

    if (questionModel.answer_options.length) {
      question['options'] = questionModel.answer_options.map((option: any) => {
        return {
          title: option.display_name,
          value: option.key,
          is_exclusive: option.is_exclusive ?? false,
          show_other_options_position:
            option.show_other_options_position ?? null,
          show_other_options_in_report:
            option.show_other_options_in_report ?? false,
        };
      });
    }

    if (
      !questionModel.answer_options.length &&
      questionModel.raw_form_options &&
      questionModel.raw_form_options.length > 0
    ) {
      question['options'] = {};
      for (const option of questionModel.raw_form_options) {
        question['options'][option.key] = option.value;
      }
    } else if (questionModel.answer_options.length) {
      question['settings'] = {};
      for (const option of questionModel.raw_form_options) {
        question['settings'][option.key] = option.value;
      }
    }

    return question;
  },
  // deprecated
  mapRules: (state, getters) => (dependency) => {
    return {
      id: dependency.id,
      object_id: dependency.object_id,
      conditions: dependency.conditions,
      condition_equation: dependency.condition_equation || '',
      loop_method: dependency.loop_method || 'none',
      replacement_text_key: dependency.replacement_text_key || '',
      is_terminated_dependency: dependency.is_terminated_dependency || false,
      questions: dependency.impacted_targets.map(getters.mapPreviewQuestion),
    };
  },
  // deprecated
  mapNegativeRules: () => (negativeRule) => {
    return {
      id: negativeRule.id,
      object_id: negativeRule.object_id,
      conditions: negativeRule.conditions,
      condition_equation: negativeRule.condition_equation || '',
      flag: negativeRule.flag || '',
      comment: negativeRule.comment || '',
    };
  },
  // deprecated
  getCurrentQuestionGroup: (state) => () => {
    return state.questionGroupQuestions[state.questionGroupIndex];
  },
  // Public Use
  // deprecated
  evalQuestionDependency: (state, getters) => (question, newAnswer) => {
    if (state.currentRule) {
      if (state.currentRule.condition_equation.length === 0) {
        return getters.evalConditionEquationIsEmpty(
          question,
          state.currentRule,
          newAnswer
        ) as boolean;
      } else {
        return getters.evalConditionEquationIsNotEmpty(
          question,
          state.currentRule,
          newAnswer
        ) as boolean;
      }
    }
  },
  // deprecated
  evalQuestionNegativeRule: (state, getters) => (question, rule) => {
    if (rule.condition_equation.length === 0) {
      return getters.evalConditionEquationIsEmpty(question, rule) as boolean;
    } else {
      return getters.evalConditionEquationIsNotEmpty(question, rule) as boolean;
    }
  },
  // deprecated
  getIterationCount: (state, getters) => (question, answer) => {
    let iterationCount = 1;
    if (state.currentRule) {
      switch (state.currentRule.loop_method) {
        case 'loop_times':
          iterationCount = parseInt(answer) || 1;
          break;
        case 'loop_answer_options':
          if (typeof answer === 'string' || answer instanceof String) {
            answer = answer.split(',');
          }
          if (Array.isArray(answer)) {
            iterationCount =
              answer.filter((a) => {
                return getters.evalQuestionDependency(question, a);
              }).length || 1;
          }
          break;
        case 'none':
        default:
          break;
      }
    }
    return iterationCount;
  },
  // deprecated
  ruleQuestions: (state, getters) => (question, answer) => {
    let questions: QQuestion[] = [];
    for (let i = 0; i < getters.getIterationCount(question, answer); i++) {
      let questionsCopy = getters.replaceTextKey(i, answer, question);

      if (i > 0) {
        questionsCopy = _.cloneDeep(questionsCopy);
      }

      if (questionsCopy) {
        questions = questions.concat(questionsCopy);
      }
    }
    return questions;
  },
  // deprecated
  replaceTextKey: (state, getters, rootState) => (
    answerIndex,
    questionAnswerValue,
    question
  ) => {
    if (!state.currentRule) {
      return [];
    }

    if (state.currentRule.questions.length === 0) {
      return [];
    }

    // new copy of impacted target questions
    let questionsCopy: QQuestion[] = [];
    questionsCopy = questionsCopy.concat(state.currentRule.questions);
    // new copy of the question answer value
    let tmpAnswerValue = questionAnswerValue;
    // default replacement text is number
    let replacement: string | number = state.iterationIndex + 1;

    switch (question.type) {
      case 'radiobutton':
      case 'checkbox':
      case 'body_parts':
      case 'dropdown_single':
      case 'dropdown_multiple':
        if (
          typeof tmpAnswerValue === 'string' ||
          tmpAnswerValue instanceof String
        ) {
          tmpAnswerValue = tmpAnswerValue.split(',');
        }
        break;
      default:
        break;
    }

    const loopedAnswerValue = tmpAnswerValue[answerIndex] as string;
    if (Array.isArray(tmpAnswerValue) && Array.isArray(question.options)) {
      switch (state.currentRule.loop_method) {
        case 'none':
          replacement = tmpAnswerValue
            .map((answerItem) => {
              // Find answer option object
              const option = question.options.find(
                (option) => option.value === answerItem
              );
              return `<strong>${option.title}</strong>`;
            })
            .join(', ');
          break;
        case 'loop_times':
          break;
        case 'loop_answer_options': {
          // Find answer option object
          const answerOption = question.options.find(
            (option) => option.value === loopedAnswerValue
          );
          if (answerOption) {
            replacement = `<strong>${answerOption['title']}</strong>`;
          }
          break;
        }
        default:
          break;
      }
    }

    return questionsCopy.map((q: QQuestion) => {
      if (
        state.currentRule &&
        state.currentRule.replacement_text_key.length > 0
      ) {
        q = JSON.parse(
          JSON.stringify(q).replaceAll(
            state.currentRule.replacement_text_key,
            replacement.toString()
          )
        );
      }

      q.parent = _.cloneDeep(
        state.questionGroupQuestions[state.questionGroupIndex][
          state.questionIndex
        ]
      );

      if (
        state.currentRule &&
        state.currentRule.loop_method === 'loop_answer_options'
      ) {
        // Add the current answer in the answer array into an additional attribute of parent object
        q.parent['looped_by_value'] = loopedAnswerValue;
        q.value['looped_by_value'] = loopedAnswerValue;
        q.value['parent_answer'] = question.value;

        if (q.value.object_id) {
          const questionAnswer = rootState[
            'QuestionnaireAnswer'
          ].questionnaireAnswer.question_answers.find(
            (qa) => qa.object_id === q.value.object_id
          );
          console.debug(q);
          if (!questionAnswer) {
            q.value = {
              answer: null,
              looped_by_value: loopedAnswerValue,
              parent_answer: question.value,
              question: q,
            };
          }
        }
      }
      return q;
    });
  },
};

const actions: ActionTree<QuestionnaireState, RootState> = {
  previousQuestion({ state, commit }) {
    commit('SET_QUESTION_INDEX', state.questionIndex - 1);
  },
  nextQuestion({ state, commit }) {
    commit('SET_QUESTION_INDEX', state.questionIndex + 1);
  },
  nextQuestionGroup({ state, commit }) {
    commit('SET_QUESTION_GROUP_INDEX', state.questionGroupIndex + 1);
    commit('SET_QUESTION_INDEX', 0);
  },
  updateQuestionGroup({ commit }, index) {
    commit('SET_QUESTION_GROUP_INDEX', index);
    commit('SET_QUESTION_INDEX', 0);
  },
  resetNextQuestions({ commit }) {
    commit('RESET_NEXT_QUESTIONS');
  },
  initQuestionGroupQuestions(
    { state, commit, dispatch },
    questionGroups = null
  ) {
    const qgs = questionGroups || state.questionnaire.question_groups;
    qgs.forEach((questionGroup, index) => {
      questionGroup.questions.forEach(async (question) => {
        // Add question into the question group question collection
        commit('ADD_QUESTION_GROUP_QUESTION', {
          questionGroupIndex: index,
          question,
        });
        // Insert question dependency questions if the current question is answered
        if (question.value.answer) {
          await dispatch('evalQuestionDependencies', {
            question,
            answer: question.value.answer,
          });
        }
      });
    });
  },
  evalQuestionDependencies(
    { state, getters, commit, dispatch },
    { question, answer }
  ) {
    commit('SET_QUESTION_GROUP_INDEX', state.questionGroupIndex);
    commit('SET_QUESTION_INDEX', state.questionIndex);

    // reset all existing next questions
    dispatch('resetNextQuestions');

    // if there is NO QuestionDependencyRule:
    //   then no need to insert new cascading quesiton
    if (!question.rules.length) {
      return false;
    }

    // if there is QuestionDependencyRule:
    // loop and evaluate each rule to see if:
    //   New casading questions need to be inserted
    //   Or should terminate current quesiton group

    // the next quesitons to be inserted into questionGroupQuesitons plain array if dependendy is matched.
    let newQuestions: QQuestion[] = [];

    // loop and evaluate each rule
    // insert nested questions according to current question rules
    for (let i = 0; i < question.rules.length; i++) {
      // let store know which rule to evaluate
      commit('SET_CURRENT_RULE', question.rules[i]);

      // let store to evaluate the QuestionDependencyRule
      if (getters.evalQuestionDependency(question, answer)) {
        // the QuestionDependencyRule is matched, calculate the isTerminated
        const hasTerminatedSetting = state.currentRule
          ? !!state.currentRule.is_terminated_dependency
          : false;
        if (hasTerminatedSetting) {
          // terminate the question group
          return true;
        }
        // if no terminated setting in this QuestionDependencyRule
        // calculate the newQuestions to be inserted
        newQuestions = newQuestions.concat(
          getters.ruleQuestions(question, answer)
        );
      }
    }

    // add new cascading questions to the store questionGroupQuestions plain array
    if (newQuestions.length > 0) {
      commit('INSERT_QUESTION_GROUP_QUESTION', newQuestions);
    }

    return false;
  },
};

const mutations: MutationTree<QuestionnaireState> = {
  SET_ORIGIN_QUESTIONNAIRE(state, payload) {
    state.originQuestionnaire = payload;
  },
  SET_QUESTIONNAIRE(state, payload) {
    state.questionnaire = payload;
  },
  SET_QUESTION_GROUP_INDEX(state, payload) {
    state.questionGroupIndex = payload;
  },
  SET_QUESTION_INDEX(state, payload) {
    state.questionIndex = payload;
  },
  SET_USER_PROFILE(state, payload) {
    state.userProfile = payload;
  },
  ADD_QUESTION_GROUP_QUESTION(state, payload) {
    state.questionGroupQuestions[payload.questionGroupIndex] =
      state.questionGroupQuestions[payload.questionGroupIndex] || [];
    state.questionGroupQuestions[payload.questionGroupIndex].push(
      payload.question
    );
  },
  SET_CURRENT_RULE(state, payload) {
    state.currentRule = payload;
  },
  RESET_NEXT_QUESTIONS(state) {
    // get current question
    const currentQuestion =
      state.questionGroupQuestions[state.questionGroupIndex][
        state.questionIndex
      ];
    // create a collection for the parent oids that need to be deleted
    const toDeleteParentOids = [currentQuestion.object_id];
    // create a collection for the question index that need to be deleted
    const toDeleteQuestionIndexes: number[] = [];
    for (const index in state.questionGroupQuestions[
      state.questionGroupIndex
    ]) {
      // check whether the question is after current question
      if (parseInt(index) <= state.questionIndex) {
        continue;
      }

      // Stop looping if the question does not have a parent question
      const question =
        state.questionGroupQuestions[state.questionGroupIndex][index];
      if (!question.parent) {
        break;
      }

      if (toDeleteParentOids.includes(question.parent.object_id)) {
        // add the question object id into the collection to search the question that is impacted by this question
        toDeleteParentOids.push(question.object_id);
        // add the question index for the filter
        toDeleteQuestionIndexes.push(parseInt(index));
      } else {
        break;
      }
    }

    // remove the questions by using the filter
    state.questionGroupQuestions[
      state.questionGroupIndex
    ] = state.questionGroupQuestions[state.questionGroupIndex].filter(
      (question, index) => !toDeleteQuestionIndexes.includes(index)
    );
  },
  INSERT_QUESTION_GROUP_QUESTION(state, payload) {
    state.questionGroupQuestions[state.questionGroupIndex].splice(
      state.questionIndex + 1,
      0,
      ...payload
    );
  },
  CLEAR_STATE(state) {
    state.questionnaire = {
      object_id: '',
      question_groups: [],
    };
    state.questionGroupIndex = 0;
    state.questionIndex = 0;
    state.iterationIndex = 0;
    state.userProfile = {
      weight: 0,
      height: 0,
      age: 0,
    };
    state.currentRule = null;
    state.questionGroupQuestions = [];
  },
  TERMINATE_QUESTION_GROUP(state) {
    state.questionGroupQuestions[state.questionGroupIndex].splice(
      state.questionIndex + 1,
      state.questionGroupQuestions[state.questionGroupIndex].length -
        (state.questionIndex + 1)
    );
  },
};

const questionnaire: Module<QuestionnaireState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default questionnaire;
